import { ParamsType, ProTable } from '@ant-design/pro-components';
import { ActionType, ProColumns, ProTableProps } from '@ant-design/pro-table/es/typing';
import {
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  FormOutlined,
  HistoryOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import React, { useMemo, useRef } from 'react';
import { Button, FormInstance } from 'antd';
import { ButtonProps } from 'antd/es/button/button';
import { AnyObject } from '@triare/auth-redux';

import { useNavigate } from 'react-router-dom';
import styles from './index.module.scss';
import useCurrentLocation from '../../../hooks/router';

export interface TableOnRowSelect {
  params?: Record<string, string>;
  selectedRows?: string[];
  onRowSelection?: ((selectedRows: string[]) => void) | undefined;
}

export interface TableListItem {
  id: string | number;
  key: string | number;
}

export interface TableProps<T, U, V> extends ProTableProps<T, U, V> {
  actions?: ([
    (props: { text: React.ReactNode, record: T, id: number, className: string }) => React.ReactNode
  ] | [
    (props: { text: React.ReactNode, record: T, id: number, className: string } & AnyObject) => React.ReactNode,
    AnyObject
  ] | [
    string,
    (props: { text: React.ReactNode, record: T, id: number }) => ProColumns<T, V>
  ] | [
    string,
    (props: { text: React.ReactNode, record: T, id: number }) => ProColumns<T, V>,
    (props: { text: React.ReactNode, record: T, id: number }) => boolean
  ])[],
  /** Used to clear filters & table data */
  resetKey: () => void;
}

export const listAction = {
  form: (props: ButtonProps) => <Button key="form" type="text" {...props} icon={<FormOutlined />} />,
  edit: ({ icon, ...props }: ButtonProps) => (
    <Button key="edit" type="text" icon={icon ?? <EditOutlined />} {...props} />
  ),
  delete: (props: ButtonProps) => <Button key="delete" type="text" {...props} icon={<DeleteOutlined />} />,
  reload: (props: ButtonProps) => <Button key="reload" type="text" {...props} icon={<ReloadOutlined />} />,
  copy: (props: ButtonProps) => <Button key="copy" type="text" {...props} icon={<CopyOutlined />} />,
  download: (props: ButtonProps) => <Button key="download" type="text" {...props} icon={<DownloadOutlined />} />,
};

function Table<T extends TableListItem, Params extends ParamsType = ParamsType, ValueType = 'text'>({
  options,
  pagination,
  columns,
  actions,
  form,
  search,
  resetKey,
  ...props
}: TableProps<T, Params, ValueType>) {
  const formRef = useRef<FormInstance>();
  const actionRef = useRef<ActionType>();

  const navigate = useNavigate();
  const currentLocation = useCurrentLocation();

  const newColumns = useMemo(() => {
    const list = (columns || []).map((column) => {
      if (column.hideInSearch === true) {
        return column;
      }

      return column;
      // column.
    });

    if (actions) {
      list.push({
        key: 'id',
        title: 'Actions',
        dataIndex: 'id',
        hideInSearch: true,
        width: actions?.length ? actions.length * 32 + 8 : 104,
        render: (text, record, id) => (
          <div className={styles.actions}>
            {actions.map(
              (action) => {
                if (typeof action[0] === 'string') {
                  const [key, createData, check] = action;

                  if ((!check || check({ text, record, id })) && typeof createData === 'function') {
                    const data = createData({ text, record, id });

                    // @ts-ignore - use the action icon in the list or use a custom element
                    return listAction[key] ? listAction[key](data) : data;
                  }
                } else {
                  const [Component, additionalProps] = action;

                  return (
                    <Component
                      key={Component.name}
                      id={id}
                      text={text}
                      record={record}
                      className={styles.actionBtn}
                      {...additionalProps}
                    />
                  );
                }

                return null;
              },
            ).filter((value) => value !== null)}
          </div>
        ),
      });
    }

    return list;
  }, []);

  return (
    <ProTable<T, Params, ValueType>
      formRef={formRef}
      actionRef={actionRef}
      className={styles.table}
      rowKey="key"
      dateFormatter="string"
      pagination={{
        defaultPageSize: 20, // 10,
        size: 'default',
        showQuickJumper: true,
        // showTotal: true,
        pageSizeOptions: [10, 20, 30, 50, 100],
        ...pagination,
      }}
      onReset={() => {
        const isSyncToUrl = form?.syncToUrl;

        if (actionRef.current && formRef.current) {
          actionRef.current?.reset?.();
          setTimeout(() => {
            resetKey();
            if (isSyncToUrl) {
              navigate(currentLocation, { replace: true });
            }
          }, 0);
          // formRef.current.resetFields?.();
          // const newValues = Object.keys(formRef.current?.getFieldsValue())
          //   .map((name) => ({
          //     name,
          //     value: undefined,
          //   }));
          //
          // formRef.current?.setFieldsValue(newValues);
        }
      }}
      columns={newColumns}
      options={{
        density: false,
        search: false,
        reloadIcon: <HistoryOutlined />,
        ...(options || {}),
      }}
      search={search ? {
        labelWidth: 'auto',
        filterType: 'query',
        ...search,
      } : false}
      form={{
        syncToUrl: true,
        ...form,
      }}
      {...props}
    />
  );
}

export default Table;
